import { BaseSyntheticEvent, useCallback, useState } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setShowCreateProviderForm,
  setShowCancelProviderForm,
} from '@Modal/reducers/providers/createProvider/CreateProviderSlice';

// Form
import { useFormState } from 'react-hook-form';
import { useCreateProviderForm } from '@Forms/providers/CreateProviderFormContext';

// System
import { useSnackbar } from 'notistack';

// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
import { useCreateProviderMutation } from '@Src/generated.graphql';

export const useCreateProvider = () => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [createProvider] = useCreateProviderMutation({
    refetchQueries: ['getProviders'],
  });

  const { showCancelProviderForm, showCreateProviderForm } = useSelector(
    (state: StoreInterface) => state?.createProvider
  );

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleCreateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelProviderForm(true));
    } else {
      return dispatch(setShowCreateProviderForm(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelProviderForm(false));
      dispatch(setShowCreateProviderForm(true));
    } else {
      reset();
      dispatch(setShowCancelProviderForm(false));
      dispatch(setShowCreateProviderForm(false));
    }
  };

  // Form Controls
  const { control, handleSubmit, reset } = useCreateProviderForm();
  const { isDirty, errors } = useFormState({ control });

  const handleSubmitCreateProvider = useCallback(
    (e?: BaseSyntheticEvent) => {
      handleSubmit(async (formData) => {
        setIsSubmitting(true);

        try {
          const newFormData = {
            ...formData,
            syncRoundEnd: formData?.syncRoundEnd === 'true',
            retryScheduledSendCredit: formData?.retryScheduledSendCredit === 'true',
            refundOnCreditFail: formData?.refundOnCreditFail === 'true',
            filterExternalProviderBets: formData?.filterExternalProviderBets === 'true',
            isInternal: formData?.isInternal === 'true',
            creditOnDisconnect: formData?.creditOnDisconnect === 'true',
          };

          await createProvider({
            // @ts-ignore
            variables: { input: newFormData },
          });

          enqueueSnackbar('Provider was successfully created', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          reset();
          dispatch(setShowCreateProviderForm(false));
        } catch (error: any) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      })(e);
    },
    [createProvider]
  );

  return {
    // Store
    showCancelProviderForm,
    showCreateProviderForm,
    submittable: isDirty && Object.values(errors).length == 0 && !isSubmitting,
    // Actions
    handleCreateCancel,
    handleCancelTakeMeBack,
    setShowCreateProviderForm,
    handleSubmitCreateProvider,
  };
};
